// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Trend view rendering: the trend summary status text, overall +
// per-signal status helpers, the severity-sorted health rail (metric
// tiles with the stable tail collapsed), the briefing items inside the
// hero band's expander, and the hero verdict band painter (the owner
// story machinery in trend-signal-summary.js is the copy source).
//
// renderTrendView orchestrator stays in app.js because it owns the
// mutable currentTrendRows / currentTrendAircraft / renderedTrend*
// signature state. This module is the content rendering layer it
// dispatches to.

import {
  trendMetricAvailable as trendMetricAvailableForRows,
  trendRowsHaveCarbIceData,
} from "../trends/trend-availability.js";
import { overallTrendVerdict } from "../trends/trend-signal-summary.js";

export function createTrendViewRenderer({
  escapeHtml,
  averageValues,
  trendDirection,
  trendAnchorIndex,
  trendWindowRows,
  trendSignalDefinitionsForRows,
  buildTrendSignalSummary,
  buildTrendSignalSummaries,
  trendSignalTitle,
  trendSignalRepeatText,
  trendDirectionText,
  trendSpecificEvidenceText,
  buildTrendOwnerStory,
  buildGamiTrendAggregate,
  getActiveTrendMetric,
  setActiveTrendMetric,
}) {
  // Whether the rail's collapsed "N systems stable" group is expanded. The group is re-rendered
  // via innerHTML on every update, so the open state must survive outside the DOM.
  let stableRailOpen = false;

  // Sticky only when the whole rail fits under the app header (86px offset + a
  // little slack). A rail taller than the viewport stays in normal flow, so the
  // page never grows a second scrollbar. Re-checked after every rail rebuild,
  // when the stable group toggles, and on window resize.
  function syncTrendRailStickiness() {
    const rail = document.querySelector(".trend-visual-grid > .trend-toolbar.panel");
    if (!rail) return;
    const fits = rail.offsetHeight <= window.innerHeight - 96;
    rail.classList.toggle("is-rail-sticky", fits);
  }
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("resize", syncTrendRailStickiness);
    syncTrendRailStickiness();
  }

  function renderTrendSummary(rows) {
    const status = document.querySelector("#trendStatus");
    if (status) status.textContent = rows.length ? "" : "No trend-ready flights";
  }

  function recurringTrendFocus(rows) {
    if (!rows.length) return { label: "--", details: [] };
    const signals = buildTrendSignalSummaries(rows);
    const top = signals[0];
    return {
      label: top ? top.label : "Clear",
      details: signals.slice(0, 4).map((signal) => ({
        label: signal.label,
        key: signal.key,
        flagged: signal.flaggedCount,
        average: signal.average,
      })),
    };
  }

  function compactTrendSignalLabel(label) {
    return String(label ?? "").replace(/\s+\(\d+\)\s*$/, "");
  }

  function trendRowsHaveCarbIce(rows) {
    return trendRowsHaveCarbIceData(rows);
  }

  function trendRowsHaveTurbo(rows) {
    return trendSignalDefinitionsForRows(rows).some((definition) => definition.key === "turboScore");
  }

  function trendRowsHaveChannel(rows, key) {
    return (Array.isArray(rows) ? rows : []).some((row) => row?.[key] === true);
  }

  function trendMetricAvailable(rows, key) {
    return trendMetricAvailableForRows(rows, key);
  }

  // Overall verdict follows the same earned-red ladder as per-signal status: Attention
  // only when a tracked area earned red (persistent AND worsening, or a sustained
  // deep-low streak) or the overall series itself did; a chronic-but-steady cue keeps
  // the page at Watch. The aggregate stays the headline — one amber area on an
  // otherwise-healthy history must not paint the whole page red.
  function overallTrendStatus(rows) {
    return overallTrendVerdict(rows, buildTrendSignalSummaries(rows));
  }

  function trendOverallTileEvidence(rows) {
    const metricRows = rows.filter((row) => Number.isFinite(row.overallScore));
    if (!metricRows.length) return "no overall score";
    const recentRows = trendWindowRows(metricRows, 10);
    const recentFlagged = recentRows.filter((row) => row.overallScore < 88).length;
    if (recentFlagged) return `${recentFlagged} of last ${recentRows.length}`;
    return `clear across ${metricRows.length}`;
  }

  function trendStatusMixBucket(status) {
    const label = String(status?.label ?? "").toLowerCase();
    const className = String(status?.className ?? "");
    if (className === "red") return "alert";
    if (label.includes("profile")) return "profile";
    if (className === "amber") return "watch";
    if (className === "green") return "stable";
    return "limited";
  }

  function trendStatusMixFromTiles(tiles) {
    const counts = { stable: 0, watch: 0, profile: 0, alert: 0, limited: 0 };
    tiles.forEach((tile) => { counts[trendStatusMixBucket(tile.status)] += 1; });
    return {
      ...counts,
      total: tiles.length,
    };
  }

  function trendStatusMixHealthScore(mix) {
    if (!mix?.total) return null;
    return Math.round(mix.stable / mix.total * 100);
  }

  function trendOverallTileSummary(rows, signalTiles) {
    const mix = trendStatusMixFromTiles(signalTiles);
    return {
      score: trendStatusMixHealthScore(mix),
      mix,
    };
  }

  function trendOverallTileSummaryHtml(summary) {
    const score = Number.isFinite(summary?.score) ? `${summary.score}%` : "--";
    const mix = summary?.mix ?? { stable: 0, watch: 0, profile: 0, alert: 0, limited: 0, total: 0 };
    const segments = [
      ["stable", "Stable", mix.stable],
      ["watch", "Watch", mix.watch],
      ["profile", "Needs profile", mix.profile],
      ["alert", "Attention", mix.alert],
      ["limited", "Limited", mix.limited],
    ].filter(([, , count]) => count > 0);
    const barSegments = segments.length ? segments : [["limited", "No tracked areas", 1]];
    const countParts = [
      `${mix.stable} stable`,
      mix.watch ? `${mix.watch} watch` : "",
      `${mix.profile} profile`,
      `${mix.alert} attention`,
    ].filter(Boolean);
    return `
      <span class="trend-overall-health">
        <b>${escapeHtml(score)}</b>
        <small>areas stable</small>
      </span>
      <span class="trend-status-mix" aria-label="${escapeHtml(countParts.join(", "))}">
        ${barSegments.map(([key, label, count]) => `
          <i class="is-${escapeHtml(key)}" style="--mix-count:${Math.max(1, Number(count) || 1)}" title="${escapeHtml(`${count} ${label}`)}"></i>
        `).join("")}
      </span>
      <span class="trend-status-counts">${escapeHtml(countParts.join(" / ") || "no tracked areas")}</span>
    `;
  }

  function trendSignalTileEvidence(signal) {
    if (!signal) return "no usable data";
    if (signal.status?.label === "Needs profile") return "profile needed";
    if (signal.recentFlagged) return `${signal.recentFlagged} of last ${signal.recentCount}`;
    if (signal.flaggedCount) return `${signal.flaggedCount} total`;
    if (signal.metricRows) return `clear across ${signal.metricRows}`;
    return "no usable data";
  }

  function trendMetricTileItems(rows) {
    if (!rows.length) return [{
      key: "overallScore",
      label: "Overall",
      status: { label: "No data", className: "muted" },
      evidence: "no trend-ready flights",
    }];
    const overall = {
      key: "overallScore",
      label: "Overall",
      status: overallTrendStatus(rows),
      evidence: trendOverallTileEvidence(rows),
    };
    const signals = trendSignalDefinitionsForRows(rows)
      .map((definition) => buildTrendSignalSummary(rows, definition))
      .filter(Boolean)
      .map((signal) => {
        const stable = !signal.flaggedCount && Number.isFinite(signal.latestScore) && signal.latestScore >= 88;
        return {
          key: signal.key,
          label: signal.label,
          status: stable ? { label: "Stable", className: "green" } : signal.status,
          evidence: trendSignalTileEvidence(signal),
        };
      })
      // Severity-sorted health rail (Phase 5c): problems first, stable last. The sort is stable,
      // so signals with equal severity keep their definition order.
      .sort((a, b) => trendTileSeverityRank(a) - trendTileSeverityRank(b));
    overall.summary = trendOverallTileSummary(rows, signals);
    return [overall, ...signals];
  }

  function trendTileSeverityRank(tile) {
    const className = tile?.status?.className;
    if (className === "red") return 0;
    if (className === "amber") return 1;
    if (className === "muted") return 2;
    return 3;
  }

  // A tile joins the collapsed "N systems stable" group only when it is green; red/amber/muted
  // (needs data or profile) tiles stay visible so missing baselines are not hidden as "stable".
  function trendTileIsStable(tile) {
    return tile?.status?.className === "green" && tile.key !== "overallScore";
  }

  function updateTrendMetricControls(rows) {
    const tiles = trendMetricTileItems(rows);
    const availableMetrics = tiles.map((tile) => tile.key);
    const currentMetric = getActiveTrendMetric();
    if (!availableMetrics.includes(currentMetric)) setActiveTrendMetric(availableMetrics[0] ?? "overallScore");
    const activeMetric = getActiveTrendMetric();
    const container = document.querySelector("#trendMetricTiles");
    if (container) {
      const tileHtml = (tile) => {
        const active = tile.key === activeMetric;
        return `
          <button class="trend-metric-tile status-${escapeHtml(tile.status.className)}${active ? " is-active" : ""}" data-trend-metric="${escapeHtml(tile.key)}" type="button" role="option" aria-selected="${active ? "true" : "false"}">
            <span class="engine-status ${escapeHtml(tile.status.className)}">${escapeHtml(tile.status.label)}</span>
            <strong>${escapeHtml(tile.label)}</strong>
            ${tile.summary ? trendOverallTileSummaryHtml(tile.summary) : `<span class="trend-metric-evidence">${escapeHtml(tile.evidence ?? "")}</span>`}
            ${active && !tile.summary ? '<span class="trend-metric-active-label">Story focus</span>' : ""}
          </button>
        `;
      };
      // Phase 5c: stable (green) systems collapse under one summary row so the rail reads as a
      // severity-sorted problem list. The stable tiles stay in the DOM as real .trend-metric-tile
      // buttons (the perf check counts and clicks them), just inside a <details>.
      // Expanded stable tiles render as dense one-line rows, not full cards — quiet
      // content stays visually small, and the rail stops towering over the chart column.
      const compactTileHtml = (tile) => {
        const active = tile.key === activeMetric;
        return `
          <button class="trend-metric-tile is-compact status-${escapeHtml(tile.status.className)}${active ? " is-active" : ""}" data-trend-metric="${escapeHtml(tile.key)}" type="button" role="option" aria-selected="${active ? "true" : "false"}" title="${escapeHtml(tile.status.label)}">
            <span class="trend-tile-dot ${escapeHtml(tile.status.className)}" aria-hidden="true"></span>
            <strong>${escapeHtml(tile.label)}</strong>
            <span class="trend-metric-evidence">${escapeHtml(tile.evidence ?? "")}</span>
          </button>
        `;
      };
      const flagged = tiles.filter((tile) => !trendTileIsStable(tile));
      const stable = tiles.filter(trendTileIsStable);
      const activeInStable = stable.some((tile) => tile.key === activeMetric);
      if (activeInStable) stableRailOpen = true;
      // Affirmative stable summary (alarm redesign): name what is steady instead of
      // hiding it behind a bare count — good news is a finding, not filler.
      const stableNames = stable.slice(0, 3).map((tile) => tile.label).join(", ");
      const stableSummary = stable.length
        ? `Stable: ${stableNames}${stable.length > 3 ? ` + ${stable.length - 3} more` : ""} — no repeating cues`
        : "";
      const stableHtml = stable.length ? `
        <details class="trend-rail-stable"${stableRailOpen ? " open" : ""}>
          <summary>${escapeHtml(stableSummary)}</summary>
          ${stable.map(compactTileHtml).join("")}
        </details>
      ` : "";
      container.innerHTML = flagged.map(tileHtml).join("") + stableHtml;
      container.querySelector("details.trend-rail-stable")?.addEventListener("toggle", (event) => {
        stableRailOpen = event.target.open;
        syncTrendRailStickiness();
      });
      syncTrendRailStickiness();
    }
    document.querySelectorAll('[data-trend-column="turbo"]').forEach((element) => {
      element.hidden = !availableMetrics.includes("turboScore");
    });
    document.querySelectorAll('[data-trend-column="carbIce"]').forEach((element) => {
      element.hidden = !trendMetricAvailable(rows, "carbIceScore");
    });
  }

  function trendFindingSummaryNote(signal) {
    const repeat = signal.recentFlagged
      ? `${signal.recentFlagged} of the last ${signal.recentCount}`
      : `${signal.flaggedCount} total`;
    const affected = signal.affected ? ` on ${signal.affected}` : "";
    return `${signal.label}${affected}: ${repeat}, ${trendDirectionText(signal.direction)}`;
  }

  function trendBriefingSignalName(signal) {
    if (!signal) return "";
    return signal.affected ? `${signal.label} on ${signal.affected}` : signal.label;
  }

  function trendFindingsSummaryParagraphs(story, usableRows) {
    const flightCount = usableRows.length;
    const flightLabel = `${flightCount} trend-ready flight${flightCount === 1 ? "" : "s"}`;
    if (!flightCount) {
      return [story.body];
    }
    if (flightCount === 1) {
      return [
        `Only one trend-ready flight is available for ${story.aircraft || "this aircraft"}, so the page can describe that flight but cannot yet separate one-off behavior from a repeatable pattern.`,
        "Upload more representative flights before treating any trend finding as meaningful.",
      ];
    }

    const signals = buildTrendSignalSummaries(usableRows);
    if (!signals.length) {
      return [
        `Across ${flightLabel}, the monitored areas stayed steady at the current sensitivity — no repeating review items.`,
        "Keep adding normal, representative flights so a future change has a cleaner baseline to stand out against.",
      ];
    }

    const top = signals[0];
    const paragraphs = [
      `Across ${flightLabel}, the strongest finding is: ${trendSignalTitle(top)}. ${trendSignalRepeatText(top)} showed this finding, and it is ${trendDirectionText(top.direction)}.`,
    ];
    const evidence = trendSpecificEvidenceText(top);
    if (evidence) paragraphs[0] = `${paragraphs[0]} ${evidence}`;

    const otherSignals = signals.slice(1, 4);
    if (otherSignals.length) {
      paragraphs.push(`Other findings to keep on the radar: ${otherSignals.map(trendFindingSummaryNote).join("; ")}.`);
    }

    paragraphs.push("Use this summary as a prioritization aid, not a maintenance diagnosis. The next useful step is to compare matching phases of flight and operating context before deciding whether an inspection or operating change is warranted.");
    return paragraphs.slice(0, 3);
  }

  function trendSummaryBriefingItems(story, usableRows) {
    const flightCount = usableRows.length;
    const flightLabel = `${flightCount} trend-ready flight${flightCount === 1 ? "" : "s"}`;
    const signals = flightCount > 1 ? buildTrendSignalSummaries(usableRows) : [];
    const primary = story.explanationSignal ?? signals[0] ?? null;

    if (!flightCount) {
      return [
        { label: "Main pattern", value: "No trend history yet", detail: story.body },
        { label: "Other signals", value: "Not available", detail: "Trend comparisons need analyzed flight rows before secondary signals are meaningful." },
        { label: "Do not assume", value: "No diagnosis", detail: "This page needs enough comparable flights before it can separate repeat behavior from one-off data." },
        { label: "Best next check", value: "Build history", detail: story.action || "Upload representative flights and run analysis." },
      ];
    }

    if (flightCount === 1) {
      return [
        { label: "Main pattern", value: "One flight only", detail: `Only one trend-ready flight is available for ${story.aircraft || "this aircraft"}.` },
        { label: "Other signals", value: "Not comparable yet", detail: "The page can describe the selected log, but it cannot confirm a repeatable trend from one flight." },
        { label: "Do not assume", value: "No trend verdict", detail: "Treat single-flight cues as review prompts until several comparable flights agree." },
        { label: "Best next check", value: "Add baseline flights", detail: "Upload more representative flights before treating any trend finding as meaningful." },
      ];
    }

    if (!signals.length) {
      return [
        { label: "Main pattern", value: "Steady across flights", detail: `Across ${flightLabel}, the monitored areas stayed steady at the current sensitivity — no repeating review items.` },
        { label: "Other signals", value: "Baseline only", detail: "No secondary signal is currently strong enough to prioritize." },
        { label: "Do not assume", value: "Keep watching", detail: "A clear trend page does not prove the engine is perfect; it means this automated screen has no repeated cue right now." },
        { label: "Best next check", value: "Maintain baseline", detail: "Keep adding normal, representative flights so future changes have a cleaner baseline." },
      ];
    }

    const secondary = signals
      .filter((signal) => signal.key !== primary?.key)
      .slice(0, 3);
    const evidence = primary ? trendSpecificEvidenceText(primary) : "";
    const items = [
      {
        label: "Main pattern",
        value: primary ? trendSignalTitle(primary) : story.title,
        detail: primary
          ? `${trendSignalRepeatText(primary)} showed this finding, and it is ${trendDirectionText(primary.direction)}.${evidence ? ` ${evidence}` : ""}`
          : story.body,
      },
      {
        label: "Other signals",
        value: secondary.length ? `${secondary.length} worth comparing` : "None elevated",
        detail: secondary.length
          ? `${secondary.map(trendBriefingSignalName).join("; ")}.`
          : "No other monitored area is currently competing with the main pattern.",
      },
      {
        label: "Do not assume",
        value: "Not a diagnosis",
        detail: "Use this as prioritization, then compare phase, power, mixture, sensor quality, and operating context before deciding on maintenance.",
      },
      {
        label: "Best next check",
        value: "Compare evidence",
        detail: story.action || story.exception?.action || "Open the clearest sample flight and compare matching phases of flight.",
      },
    ];
    // When the verdict band carries a dedicated exception card, the "Main pattern"
    // briefing item would annunciate the same condition a second time — drop it.
    return story.exception ? items.slice(1) : items;
  }

  function renderTrendFindingsSummary(story, usableRows) {
    const summary = document.querySelector("#trendFindingsSummary");
    if (!summary) return;
    const items = trendSummaryBriefingItems(story, usableRows)
      .map((item) => ({
        label: String(item.label ?? "").trim(),
        value: String(item.value ?? "").trim(),
        detail: String(item.detail ?? "").trim(),
      }))
      .filter((item) => item.label && (item.value || item.detail))
      .slice(0, 4);
    summary.innerHTML = (items.length ? items : [{ label: "Summary", value: "Not available", detail: "Trend findings summary is not available yet." }])
      .map((item) => `
        <article class="trend-briefing-item">
          <span>${escapeHtml(item.label)}</span>
          <strong>${escapeHtml(item.value)}</strong>
          <p>${escapeHtml(item.detail)}</p>
        </article>
      `)
      .join("");
  }

  function trendGamiGph(value, { signed = false, digits = 2 } = {}) {
    if (!Number.isFinite(value)) return "--";
    const prefix = signed && value > 0 ? "+" : "";
    return `${prefix}${Number(value).toFixed(digits)} GPH`;
  }

  function trendGamiSpreadClass(value) {
    if (!Number.isFinite(value)) return "muted";
    if (value <= 0.5) return "green";
    if (value <= 0.7) return "amber";
    return "red";
  }

  function trendGamiDirectionClass(key) {
    if (key === "narrowing" || key === "stable") return "green";
    if (key === "widening") return "red";
    return "amber";
  }

  function trendGamiCoverageText(aggregate) {
    const stats = aggregate.stats;
    const sweepText = `${stats.aggregateSweepCount} aggregate sweep${stats.aggregateSweepCount === 1 ? "" : "s"}`;
    const flightText = stats.flightsWithSweeps
      ? `across ${stats.flightsWithSweeps} flight${stats.flightsWithSweeps === 1 ? "" : "s"}`
      : "";
    const limited = [
      stats.lowConfidenceCount ? `${stats.lowConfidenceCount} low confidence` : "",
      stats.partialCount ? `${stats.partialCount} partial` : "",
    ].filter(Boolean).join(", ");
    return `${sweepText}${flightText ? ` ${flightText}` : ""}${limited ? ` · ${limited}` : ""}`;
  }

  function trendGamiSpreadChartSvg(aggregate) {
    const sweeps = aggregate.chartSweeps;
    if (!sweeps.length) return '<p class="trend-gami-empty">No saved GAMI spread values are available.</p>';
    const width = 820;
    const height = 260;
    const left = 54;
    const right = 22;
    const top = 24;
    const bottom = 42;
    const plotWidth = width - left - right;
    const plotHeight = height - top - bottom;
    const maxSpread = Math.max(1.05, ...sweeps.map((sweep) => sweep.spreadGph).filter(Number.isFinite), aggregate.stats.spreadMaxGph ?? 0);
    const yMax = Math.ceil((maxSpread + 0.05) * 10) / 10;
    const xFor = (index) => left + (sweeps.length === 1 ? plotWidth / 2 : (index / (sweeps.length - 1)) * plotWidth);
    const yFor = (value) => top + (1 - Math.min(1, Math.max(0, value / yMax))) * plotHeight;
    const points = sweeps.map((sweep, index) => ({ sweep, x: xFor(index), y: yFor(sweep.spreadGph) }));
    const path = points.map((point, index) => `${index ? "L" : "M"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(" ");
    const thresholds = [
      { value: 0.5, label: "0.5 target", className: "target" },
      { value: 0.7, label: "0.7 watch", className: "watch" },
      { value: 1.0, label: "1.0 review", className: "review" },
    ].filter((item) => item.value <= yMax);
    const labelIndexes = [...new Set([0, Math.floor((sweeps.length - 1) / 2), sweeps.length - 1])]
      .filter((index) => index >= 0 && index < sweeps.length);
    return `
      <svg class="trend-gami-spread-svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="GAMI spread across saved sweeps">
        <rect class="trend-gami-plot-bg" x="${left}" y="${top}" width="${plotWidth}" height="${plotHeight}" rx="6"></rect>
        ${thresholds.map((threshold) => {
          const y = yFor(threshold.value);
          return `
            <line class="trend-gami-threshold is-${threshold.className}" x1="${left}" x2="${width - right}" y1="${y.toFixed(1)}" y2="${y.toFixed(1)}"></line>
            <text class="trend-gami-threshold-label" x="${width - right - 4}" y="${(y - 5).toFixed(1)}" text-anchor="end">${escapeHtml(threshold.label)}</text>
          `;
        }).join("")}
        <line class="trend-gami-axis" x1="${left}" x2="${left}" y1="${top}" y2="${height - bottom}"></line>
        <line class="trend-gami-axis" x1="${left}" x2="${width - right}" y1="${height - bottom}" y2="${height - bottom}"></line>
        <text class="trend-gami-axis-label" x="8" y="${top + 5}">${escapeHtml(`${yMax.toFixed(1)} GPH`)}</text>
        <text class="trend-gami-axis-label" x="18" y="${height - bottom}">0</text>
        ${path ? `<path class="trend-gami-spread-line" d="${path}"></path>` : ""}
        ${points.map((point) => `
          <g class="trend-gami-point status-${trendGamiSpreadClass(point.sweep.spreadGph)}">
            <circle cx="${point.x.toFixed(1)}" cy="${point.y.toFixed(1)}" r="5.5"></circle>
            <title>${escapeHtml(`${point.sweep.dateLabel || "Saved sweep"} ${point.sweep.engineLabel}: ${trendGamiGph(point.sweep.spreadGph)}`)}</title>
          </g>
        `).join("")}
        ${labelIndexes.map((index) => {
          const point = points[index];
          const label = point.sweep.dateLabel || `#${index + 1}`;
          return `<text class="trend-gami-x-label" x="${point.x.toFixed(1)}" y="${height - 14}" text-anchor="${index === 0 ? "start" : index === sweeps.length - 1 ? "end" : "middle"}">${escapeHtml(label)}</text>`;
        }).join("")}
      </svg>
    `;
  }

  function trendGamiCylinderChartHtml(aggregate) {
    const cylinders = aggregate.cylinderSummaries;
    if (!cylinders.length) {
      return '<p class="trend-gami-empty">Saved sweeps do not include cylinder peak FF detail yet.</p>';
    }
    const maxAbs = Math.max(0.1, ...cylinders.map((item) => Math.abs(item.avgDeltaGph ?? 0)));
    return `
      <div class="trend-gami-cylinder-bars" aria-label="Cylinder average peak fuel flow delta from sweep median">
        ${cylinders.map((item) => {
          const delta = Number.isFinite(item.avgDeltaGph) ? item.avgDeltaGph : 0;
          const width = Math.min(46, Math.abs(delta) / maxAbs * 46);
          const left = delta >= 0 ? 50 : 50 - width;
          const direction = item.direction === "early" ? "early" : item.direction === "late" ? "late" : "near";
          return `
            <div class="trend-gami-cylinder-bar-row is-${direction}">
              <span class="trend-gami-cylinder-name">${escapeHtml(item.label)}</span>
              <span class="trend-gami-cylinder-track" style="--gami-bar-left:${left.toFixed(2)}%;--gami-bar-width:${width.toFixed(2)}%;">
                <i></i>
              </span>
              <strong>${escapeHtml(trendGamiGph(item.avgDeltaGph, { signed: true }))}</strong>
            </div>
          `;
        }).join("")}
      </div>
    `;
  }

  function trendGamiCylinderTableHtml(aggregate) {
    const cylinders = aggregate.cylinderSummaries;
    if (!cylinders.length) return "";
    return `
      <div class="trend-gami-table-wrap">
        <table class="trend-gami-table">
          <thead>
            <tr>
              <th>Cyl</th>
              <th>Avg peak FF</th>
              <th>Avg delta</th>
              <th>Peak order</th>
              <th>First</th>
              <th>Last</th>
              <th>Recent move</th>
            </tr>
          </thead>
          <tbody>
            ${cylinders.map((item) => {
              const move = item.drift === "earlier"
                ? "Earlier"
                : item.drift === "later" ? "Later" : "Stable";
              return `
                <tr>
                  <td>${escapeHtml(item.label)}</td>
                  <td>${escapeHtml(trendGamiGph(item.avgPeakFuelFlowGph))}</td>
                  <td>${escapeHtml(trendGamiGph(item.avgDeltaGph, { signed: true }))}</td>
                  <td>${Number.isFinite(item.avgPeakOrder) ? escapeHtml(item.avgPeakOrder.toFixed(1)) : "--"}</td>
                  <td>${escapeHtml(`${item.firstCount}/${item.count}`)}</td>
                  <td>${escapeHtml(`${item.lastCount}/${item.count}`)}</td>
                  <td>${escapeHtml(move)}</td>
                </tr>
              `;
            }).join("")}
          </tbody>
        </table>
      </div>
    `;
  }

  function trendGamiSweepDetailTableHtml(aggregate) {
    const sweeps = aggregate.chartSweeps.slice().reverse().slice(0, 12);
    if (!sweeps.length) return "";
    return `
      <div class="trend-gami-table-wrap">
        <table class="trend-gami-table trend-gami-sweep-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Engine</th>
              <th>Spread</th>
              <th>Confidence</th>
              <th>Peaks</th>
              <th>First</th>
              <th>Last</th>
            </tr>
          </thead>
          <tbody>
            ${sweeps.map((sweep) => `
              <tr>
                <td>${escapeHtml(sweep.dateLabel || "Unknown")}</td>
                <td>${escapeHtml(sweep.engineLabel || "Engine")}</td>
                <td><span class="engine-status ${trendGamiSpreadClass(sweep.spreadGph)}">${escapeHtml(trendGamiGph(sweep.spreadGph))}</span></td>
                <td>${escapeHtml(sweep.confidence || "--")}</td>
                <td>${Number.isFinite(sweep.clearPeakCount) && Number.isFinite(sweep.peakCount) ? escapeHtml(`${sweep.clearPeakCount}/${sweep.peakCount}`) : "--"}</td>
                <td>${escapeHtml(sweep.firstPeakLabel || "--")}</td>
                <td>${escapeHtml(sweep.lastPeakLabel || "--")}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;
  }

  function trendGamiAggregateHtml(aggregate) {
    const stats = aggregate.stats;
    const directionClass = trendGamiDirectionClass(aggregate.trend.key);
    return `
      <div class="trend-gami-verdict">
        <div>
          <span>Aggregate spread</span>
          <strong>${escapeHtml(trendGamiGph(stats.spreadAverageGph))}</strong>
          <small>median ${escapeHtml(trendGamiGph(stats.spreadMedianGph))}</small>
        </div>
        <div>
          <span>Range</span>
          <strong>${escapeHtml(`${trendGamiGph(stats.spreadMinGph)}-${trendGamiGph(stats.spreadMaxGph)}`)}</strong>
          <small>${escapeHtml(trendGamiCoverageText(aggregate))}</small>
        </div>
        <div>
          <span>Latest</span>
          <strong>${escapeHtml(trendGamiGph(stats.latestSpreadGph))}</strong>
          <small>${escapeHtml(stats.latestLabel || "latest saved sweep")}</small>
        </div>
        <div>
          <span>Direction</span>
          <strong class="${escapeHtml(directionClass)}">${escapeHtml(aggregate.trend.label)}</strong>
          <small>${escapeHtml(aggregate.trend.detail)}</small>
        </div>
        <div>
          <span>Driver</span>
          <strong>${escapeHtml(aggregate.driver.label)}</strong>
          <small>${escapeHtml(aggregate.driver.detail)}</small>
        </div>
      </div>
      <div class="trend-gami-aggregate-grid">
        <section class="trend-gami-chart-block">
          <header>
            <span>Spread over time</span>
            <strong>${escapeHtml(aggregate.trend.label)}</strong>
          </header>
          ${trendGamiSpreadChartSvg(aggregate)}
        </section>
        <section class="trend-gami-chart-block">
          <header>
            <span>Cylinder peak FF pattern</span>
            <strong>${escapeHtml(aggregate.driver.label)}</strong>
          </header>
          ${trendGamiCylinderChartHtml(aggregate)}
        </section>
      </div>
      <div class="trend-gami-detail-grid">
        <section>
          <header>
            <span>Cylinder aggregate</span>
            <strong>Peak FF and order across sweeps</strong>
          </header>
          ${trendGamiCylinderTableHtml(aggregate)}
        </section>
        <section>
          <header>
            <span>Saved sweep details</span>
            <strong>Flights included in this GAMI aggregate</strong>
          </header>
          ${trendGamiSweepDetailTableHtml(aggregate)}
        </section>
      </div>
    `;
  }

  function renderTrendGamiAggregate(story, usableRows) {
    const panel = document.querySelector("#trendGamiAggregatePanel");
    const content = document.querySelector("#trendGamiAggregateContent");
    if (!panel || !content) return;
    const show = getActiveTrendMetric() === "gamiSweepScore" || story.explanationSignal?.key === "gamiSweepScore";
    if (!show || typeof buildGamiTrendAggregate !== "function") {
      panel.hidden = true;
      content.innerHTML = "";
      return;
    }
    const aggregate = buildGamiTrendAggregate(usableRows);
    if (!aggregate.chartSweeps.length) {
      panel.hidden = true;
      content.innerHTML = "";
      return;
    }
    panel.hidden = false;
    content.innerHTML = trendGamiAggregateHtml(aggregate);
  }

  function trendStoryEvidenceLabel(story, usableRows) {
    const signal = story.explanationSignal;
    if (signal?.flaggedCount) return trendSignalRepeatText(signal);
    if (usableRows.length > 1 && story.status === "Stable") {
      return `${usableRows.length} trend-ready flights with no repeated item`;
    }
    if (usableRows.length === 1) return "1 trend-ready flight";
    if (usableRows.length) return `${usableRows.length} trend-ready flights`;
    return "No trend-ready flights";
  }

  // The verdict is split across two fixed surfaces (alarm redesign): the one-line
  // ribbon at the top of the page carries the answer (status badge + headline + window
  // meta) in the same spot every visit, and the findings band below the trend picture
  // carries the reading material (AI summary, body, next step, exception card,
  // briefing). The owner-story machinery in trend-signal-summary.js stays the copy
  // source for both.
  function renderTrendOwnerStory(allRows, usableRows, aircraft) {
    const story = buildTrendOwnerStory(allRows, usableRows, aircraft);
    const ribbon = document.querySelector("#trendVerdictRibbon");
    const band = document.querySelector("#trendVerdictBand");
    const status = document.querySelector("#trendVerdictStatus");
    const coverage = document.querySelector("#trendVerdictCoverage");
    const window = document.querySelector("#trendVerdictWindow");
    const title = document.querySelector("#trendVerdictTitle");
    const body = document.querySelector("#trendVerdictBody");
    const evidence = document.querySelector("#trendVerdictEvidence");
    const action = document.querySelector("#trendVerdictAction");
    if (ribbon) ribbon.className = `trend-verdict-ribbon status-${story.className}`;
    if (band) band.className = "trend-verdict-band trend-findings-band";
    if (status) {
      status.textContent = story.status;
      status.className = `engine-status ${story.className}`;
    }
    if (coverage) coverage.textContent = story.coverage;
    if (window) window.textContent = story.windowLabel;
    if (title) title.textContent = story.title;
    if (body) body.textContent = story.body;
    if (evidence) evidence.textContent = trendStoryEvidenceLabel(story, usableRows);
    if (action) action.textContent = story.action;
    renderTrendVerdictException(story);
    renderTrendFindingsSummary(story, usableRows);
    renderTrendGamiAggregate(story, usableRows);
  }

  // The single exception card (alarm redesign): when the verdict leads with the
  // aggregate, the top signal is annunciated exactly once, here. Every other
  // surface (rail tile, briefing, status mix) references it without repeating it.
  function renderTrendVerdictException(story) {
    const exception = document.querySelector("#trendVerdictException");
    if (!exception) return;
    if (!story.exception) {
      exception.hidden = true;
      exception.className = "trend-verdict-exception";
      exception.innerHTML = "";
      return;
    }
    exception.hidden = false;
    exception.className = `trend-verdict-exception status-${story.exception.className}`;
    exception.innerHTML = `
      <span class="engine-status ${escapeHtml(story.exception.className)}">${escapeHtml(story.exception.status)}</span>
      <div class="trend-verdict-exception-copy">
        <strong>${escapeHtml(story.exception.title)}</strong>
        <p>${escapeHtml(story.exception.body)}</p>
        ${story.exception.action ? `<p class="trend-verdict-exception-action">${escapeHtml(story.exception.action)}</p>` : ""}
      </div>
    `;
  }

  return {
    renderTrendSummary,
    recurringTrendFocus,
    compactTrendSignalLabel,
    trendRowsHaveCarbIce,
    trendRowsHaveTurbo,
    trendRowsHaveChannel,
    trendMetricAvailable,
    overallTrendStatus,
    trendOverallTileEvidence,
    trendSignalTileEvidence,
    trendMetricTileItems,
    updateTrendMetricControls,
    trendFindingSummaryNote,
    trendBriefingSignalName,
    trendFindingsSummaryParagraphs,
    trendSummaryBriefingItems,
    renderTrendFindingsSummary,
    trendStoryEvidenceLabel,
    renderTrendGamiAggregate,
    renderTrendOwnerStory,
  };
}
